import { useCallback } from 'react'

import emDash from '~/components/emdash'

import { isEmissionsUnit } from '../types/units'
import { useNumberFormatter } from './number'
import type { FormatterHook } from './types'
import { combineOptions } from './types'
import { useUnitsFormatter } from './units'

const defaultOptions = {
  longForm: false,
  sigFigs: 1,
  units: 'kg of CO2',
  omitUnits: false,
  invalidStr: emDash
}

const exportOptions = { ...defaultOptions, omitUnits: true, invalidStr: '' }

// eslint-disable-next-line import/prefer-default-export
export const useEmissionsFormatter: FormatterHook = () => {
  const formatNumber = useNumberFormatter()
  const formatUnits = useUnitsFormatter()

  return useCallback(
    (value, options) => {
      const {
        value: emissions,
        longForm,
        sigFigs,
        units,
        omitUnits,
        invalidStr
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      if (!isEmissionsUnit(units)) return invalidStr
      if (typeof emissions !== 'string' && typeof emissions !== 'number') return invalidStr

      // Coerce to number
      const emissionsNum = parseFloat(`${emissions}`)
      if (isNaN(emissionsNum)) return invalidStr

      const emissionsStr = formatNumber(emissionsNum, { maximumFractionDigits: sigFigs, export: options?.export })
      if (omitUnits) return emissionsStr

      const formattedUnits = formatUnits.emissions(units, longForm ? 'long' : 'short')
      return [emissionsStr, formattedUnits].join(' ')
    },
    [formatNumber, formatUnits]
  )
}
